import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Sparkles, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { AFFIRMATION } from "@/constants/testIds";

export default function Affirmations() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => api.get("/affirmations/saved").then(({ data }) => setItems(data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    await api.post(`/affirmations/${id}/save`);
    setItems((prev) => prev.filter((a) => a.affirmation_id !== id));
    toast.success("Removed from your collection.");
  };

  return (
    <div className="space-y-6 max-w-2xl mx-auto" data-testid={AFFIRMATION.page}>
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-[#C89B55] font-semibold">Your collection</p>
        <h1 className="font-display text-3xl lg:text-4xl font-semibold mt-1">Saved affirmations</h1>
        <p className="text-[#59535B] mt-2">The words you want to keep close. Return to them whenever you need them.</p>
      </div>

      {loading ? (
        <p className="text-sm text-[#8A838E]">Loading…</p>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center justify-center p-12 text-center bg-white rounded-3xl border border-[#291B2D]/5" data-testid="affirmations-empty">
          <span className="w-14 h-14 rounded-full bg-[#F1EDE0] text-[#C89B55] flex items-center justify-center mb-3"><Sparkles className="h-6 w-6" /></span>
          <p className="font-display text-lg">No saved affirmations yet</p>
          <p className="text-sm text-[#8A838E] mt-1 max-w-sm">Tap the heart on your daily affirmation to keep it here.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((a, i) => (
            <motion.div key={a.affirmation_id} data-testid={`saved-affirmation-${a.affirmation_id}`}
              initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25, delay: i * 0.03 }}
              className="flex items-start gap-4 rounded-2xl bg-[#352238] text-[#FCFAF6] p-6">
              <Sparkles className="h-5 w-5 text-[#C89B55] shrink-0 mt-1" />
              <p className="font-display text-lg lg:text-xl leading-snug flex-1">"{a.text}"</p>
              <button data-testid={`saved-affirmation-remove-${a.affirmation_id}`} onClick={() => remove(a.affirmation_id)}
                aria-label="Remove affirmation" className="shrink-0 text-[#FCFAF6]/50 hover:text-[#FCFAF6] transition-smooth">
                <Trash2 className="h-4 w-4" />
              </button>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
